import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, parseMoney, qty as fmtQty } from '../lib/format.ts';
import { usePlaces } from '../lib/places.tsx';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { PlannerTab } from './MrpPlanner.tsx';
import { Bars, downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Production: what each product is made of (bill of materials, with its work steps), orders that
 * plan a quantity, start it, time the work and finish it, and what the product cost to make.
 * Finishing takes the materials out of stock and puts the product in at that cost.
 */

type State = 'planned' | 'in_progress' | 'done' | 'cancelled';
type Filter = 'open' | 'planned' | 'in_progress' | 'done' | 'cancelled' | 'all';
type Tab = 'overview' | 'orders' | 'planner' | 'boms' | 'unbuild' | 'workcenters' | 'reports';
const UNITS = ['pcs', 'kg', 'g', 'l', 'ml', 'm', 'm2', 'm3', 't'] as const;
type Unit = (typeof UNITS)[number];

interface OrderRow {
  id: string;
  number: number;
  bom: string;
  item: string;
  item_name: string;
  unit: Unit;
  bom_name: string;
  qty: number;
  state: State;
  planned_on: string;
  location: string | null;
  location_name: string | null;
  lot: string | null;
  parent: string | null;
  parent_number: number | null;
  produced: number | null;
  scrap: number | null;
  materials: number;
  labour: number;
  overhead: number;
  cost: number;
  unit_cost: number;
  finished_at: string | null;
  cancel_reason: string | null;
  steps: number;
  steps_done: number;
  late?: boolean;
  short?: boolean;
}
interface Shortage {
  item: string;
  name: string;
  unit: Unit;
  supplier: string | null;
  needed: number;
  on_hand: number;
  short: number;
  value: number;
  made_here: boolean;
  orders: { id: string; number: number; planned_on: string }[];
}
interface Overview {
  today: string;
  counts: { planned: number; in_progress: number; late: number; today: number; short: number };
  month: { orders: number; cost: number; scrap_pct: number };
  open: OrderRow[];
  recent: OrderRow[];
  shortages: Shortage[];
  load: { workcenter: string; name: string; capacity: number; days: { date: string; minutes: number }[] }[];
}
interface Detail {
  order: OrderRow & { note: string | null; started_at: string | null };
  item: { id: string; name: string; unit: Unit; on_hand: number; unit_cost: number };
  bom: { id: string; name: string; output_qty: number; overhead: number; active: boolean };
  location: { id: string; name: string };
  lines: { id: string; item: string; name: string; unit: Unit; planned: number; consumed: number | null; unit_cost: number; value: number; on_hand: number; short: number; made_here: boolean; current_cost: number }[];
  steps: { id: string; name: string; workcenter_name: string | null; cost_per_hour: number; planned_minutes: number; minutes: number | null; done_at: string | null; done_by_name: string | null }[];
  estimate: Cost | null;
  started_by_name: string | null;
  finished_by_name: string | null;
  parent: { id: string; number: number } | null;
  children: OrderRow[];
  moves: { id: string; kind: 'consume' | 'produce'; qty: number; value: number; item_name: string; unit: Unit }[];
  unbuilt: number;
}
interface UnbuildPreview {
  order: { id: string; number: number; produced: number; left: number } | null;
  bom: { id: string; name: string };
  item: { id: string; name: string; unit: Unit; unit_cost: number; on_hand: number };
  location: { id: string; name: string };
  max: number;
  parts: { item: string; name: string; unit: Unit; per_unit: number }[];
}
interface UnbuildRow {
  id: string;
  number: number;
  item_name: string;
  unit: Unit;
  qty: number;
  value: number;
  reason: string;
  at: string;
  order: string | null;
  order_number: number | null;
  bom_name: string;
  location_name: string | null;
  by_name: string | null;
  lines: { item: string; name: string; unit: Unit; expected: number; qty: number; value: number }[];
}
interface Cost {
  materials: number;
  labour: number;
  overhead: number;
  total: number;
  per_unit: number;
}
interface BomRow {
  id: string;
  item: string;
  item_name: string;
  unit: Unit;
  name: string;
  output_qty: number;
  overhead: number;
  note: string | null;
  active: boolean;
  on_hand: number;
  stock_cost: number;
  per_unit: number;
  batch_cost: number;
  materials: number;
  steps: number;
  orders: number;
}
interface BomDetail {
  bom: BomRow;
  item: { id: string; name: string; unit: Unit; on_hand: number; unit_cost: number };
  lines: { id: string; item: string; name: string; unit: Unit; per_batch: number; qty: number; scrap_pct: number; unit_cost: number; value: number; made_here: boolean }[];
  steps: { id: string; name: string; workcenter: string | null; workcenter_name: string | null; per_batch: number; minutes: number; cost: number }[];
  cost: Cost;
  qty: number;
  used_in: { id: string; name: string; item_name: string }[];
}
interface WorkCenter {
  id: string;
  name: string;
  cost_per_hour: number;
  hours_per_day: number;
  note: string | null;
  active: boolean;
  boms: number;
  open_minutes: number;
}
interface StockItem {
  id: string;
  name: string;
  unit: Unit;
  unit_cost: number;
  on_hand: number;
  made_here: boolean;
}
interface Report {
  from: string;
  to: string;
  totals: { orders: number; materials: number; labour: number; overhead: number; cost: number; scrap_pct: number; hours: number };
  by_item: { item: string; name: string; unit: Unit; orders: number; produced: number; scrap: number; cost: number; unit_cost: number }[];
  materials: { item: string; name: string; unit: Unit; planned: number; consumed: number; value: number; variance: number }[];
  work: { workcenter: string | null; name: string | null; planned_hours: number; hours: number }[];
  days: { date: string; orders: number; cost: number }[];
}

const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'overview', icon: 'factory' },
  { key: 'orders', icon: 'list' },
  { key: 'planner', icon: 'sparkle' },
  { key: 'boms', icon: 'layers' },
  { key: 'unbuild', icon: 'undo' },
  { key: 'workcenters', icon: 'settings' },
  { key: 'reports', icon: 'chart' },
];
const FILTERS: Filter[] = ['open', 'planned', 'in_progress', 'done', 'cancelled', 'all'];
const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  planned: { tone: 'info', icon: 'calendar' },
  in_progress: { tone: 'primary', icon: 'flame' },
  done: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};

export const moName = (n: number) => `MO-${n}`;
const unitLabel = (u: Unit) => t(`stock.unit_${u}` as MessageKey);
const amount = (n: number, u: Unit) => `${fmtQty(n)} ${unitLabel(u)}`;
const lek = (minor: number) => (minor ? String(minor / 100) : '');
const hours = (minutes: number) => (minutes >= 60 ? t('mrp.h_min', { h: Math.floor(minutes / 60), m: Math.round(minutes % 60) }) : t('mrp.min', { n: Math.round(minutes) }));
const oneDec = (n: number) => fmtQty(Math.round(n * 10) / 10);
const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const short = (d: string) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
const weekday = (d: string) => new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
const num = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return s.trim() && Number.isFinite(n) ? n : null;
};
const StateBadge = ({ s, late }: { s: State; late?: boolean }) =>
  late ? <StatusBadge tone="warn" icon="clock" label={t('mrp.late')} /> : <StatusBadge tone={STATE[s].tone} icon={STATE[s].icon} label={t(`mrp.st_${s}` as MessageKey)} />;

export function Manufacturing() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const { can } = useSession();
  const { data: o, reload } = useAction<Overview>('mrp.overview');
  const [planning, setPlanning] = useState<{ bom?: string; qty?: number } | null>(null);
  const [version, setVersion] = useState(0);
  const open = params.get('order');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const show = (id: string | null) => set({ order: id });

  return (
    <Shell>
      <div className="page reports mrp">
        <div className="page-head">
          <div>
            <h1>{t('mrp.title')}</h1>
            <p className="muted">{t('mrp.subtitle')}</p>
          </div>
          {can('mrp.plan') ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setPlanning({})}>
                {t('mrp.plan')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('mrp.at_a_glance')}>
            <Kpi label={t('mrp.k_in_progress')} value={String(o.counts.in_progress)} hint={tn('mrp.k_planned', o.counts.planned)} />
            <Kpi label={t('mrp.k_today')} value={String(o.counts.today)} hint={o.counts.late ? tn('mrp.k_late', o.counts.late) : t('mrp.k_none_late')} warn={o.counts.late > 0} />
            <Kpi label={t('mrp.k_short')} value={String(o.counts.short)} hint={o.shortages.length ? tn('mrp.k_materials_short', o.shortages.length) : t('mrp.k_all_there')} warn={o.counts.short > 0} />
            <Kpi label={t('mrp.k_month')} value={money(o.month.cost)} hint={[tn('mrp.k_orders_done', o.month.orders), o.month.scrap_pct ? t('mrp.k_scrap', { pct: o.month.scrap_pct }) : null].filter(Boolean).join(' · ')} />
          </section>
        )}

        <div className="segmented tabs mrp-tabs" role="tablist" aria-label={t('mrp.sections')}>
          {TABS.filter((x) => x.key !== 'reports' || can('mrp.reports')).map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key, state: null, q: null })}>
              <Icon name={x.icon} size={16} />
              {t(`mrp.tab_${x.key}` as MessageKey)}
              {x.key === 'orders' && o && o.counts.planned + o.counts.in_progress ? <span className="count">{o.counts.planned + o.counts.in_progress}</span> : null}
            </button>
          ))}
        </div>

        {!o ? null : tab === 'overview' ? (
          <OverviewTab o={o} onOpen={show} />
        ) : tab === 'orders' ? (
          <OrdersTab key={version} params={params} set={set} onOpen={show} />
        ) : tab === 'planner' ? (
          <PlannerTab onDone={changed} />
        ) : tab === 'boms' ? (
          <BomsTab onPlan={can('mrp.plan') ? (bom) => setPlanning({ bom }) : undefined} />
        ) : tab === 'unbuild' ? (
          <UnbuildTab key={version} onOpen={show} onChange={changed} />
        ) : tab === 'workcenters' ? (
          <WorkCentersTab />
        ) : (
          <ReportsTab today={o.today} />
        )}
      </div>
      {planning ? (
        <PlanDialog
          initial={planning}
          today={o?.today ?? null}
          onClose={() => setPlanning(null)}
          onDone={async (id) => {
            setPlanning(null);
            await changed();
            show(id);
          }}
        />
      ) : null}
      {open ? <OrderDialog key={open} id={open} onClose={() => show(null)} onOpen={show} onChange={changed} /> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Overview */

function OverviewTab({ o, onOpen }: { o: Overview; onOpen: (id: string) => void }) {
  const now = o.open.filter((x) => x.state === 'in_progress');
  const next = o.open.filter((x) => x.state === 'planned');
  return (
    <>
      <div className="mrp-two">
        <OrderList title={t('mrp.in_production')} icon="flame" rows={now} empty={t('mrp.nothing_on')} onOpen={onOpen} />
        <OrderList title={t('mrp.coming_up')} icon="calendar" rows={next} empty={t('mrp.nothing_planned')} onOpen={onOpen} />
      </div>
      <div className="mrp-two">
        <section className="panel" aria-labelledby="mrp-short">
          <h2 id="mrp-short" className="htl-desk-title">
            <Icon name="alert" size={18} /> {t('mrp.shortages')}
            <span className="count">{o.shortages.length}</span>
          </h2>
          {o.shortages.length ? (
            <div className="list-card">
              {o.shortages.map((s) => (
                <div key={s.item} className="list-row">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name={s.made_here ? 'factory' : 'truck'} size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{s.name}</span>
                    <span className="list-sub">
                      {[t('mrp.short_needs', { need: amount(s.needed, s.unit), have: amount(Math.max(s.on_hand, 0), s.unit) }), s.made_here ? t('mrp.made_here') : s.supplier].filter(Boolean).join(' · ')}
                    </span>
                    <span className="list-sub mrp-links">
                      {s.orders.map((x) => (
                        <button key={x.id} className="link" onClick={() => onOpen(x.id)}>
                          {moName(x.number)}
                        </button>
                      ))}
                    </span>
                  </span>
                  <span className="vat-party mrp-num">
                    <span className="tabular strong warn-text">−{amount(s.short, s.unit)}</span>
                    <span className="muted tabular">{money(s.value)}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted htl-empty">{t('mrp.no_shortages')}</p>
          )}
        </section>
        <section className="panel" aria-labelledby="mrp-load">
          <h2 id="mrp-load" className="htl-desk-title">
            <Icon name="clock" size={18} /> {t('mrp.load')}
          </h2>
          {o.load.length ? (
            <div className="mrp-load" role="table" aria-label={t('mrp.load')}>
              <div className="mrp-load-row head" role="row">
                <span role="columnheader">{t('mrp.workcenter')}</span>
                {o.load[0]!.days.map((d) => (
                  <span key={d.date} role="columnheader" className={d.date === o.today ? 'today' : ''}>
                    {d.date === o.today ? t('mrp.today') : weekday(d.date)}
                  </span>
                ))}
              </div>
              {o.load.map((w) => (
                <div key={w.workcenter} className="mrp-load-row" role="row">
                  <span role="rowheader" className="strong">
                    {w.name}
                  </span>
                  {w.days.map((d) => {
                    const pct = w.capacity ? Math.round((d.minutes / w.capacity) * 100) : 0;
                    return (
                      <span key={d.date} role="cell" className={`mrp-load-cell${pct > 100 ? ' over' : pct ? ' some' : ''}`} title={`${w.name} · ${day(d.date)} · ${hours(d.minutes)} / ${hours(w.capacity)}`}>
                        <i style={{ height: `${Math.min(100, pct)}%` }} />
                        <b className="tabular">{d.minutes ? `${pct}%` : ''}</b>
                      </span>
                    );
                  })}
                </div>
              ))}
              <p className="muted small">{t('mrp.load_hint')}</p>
            </div>
          ) : (
            <p className="muted htl-empty">{t('mrp.no_workcenters')}</p>
          )}
        </section>
      </div>
      {o.recent.length ? (
        <section className="panel" aria-labelledby="mrp-recent">
          <h2 id="mrp-recent" className="rep-sub">
            {t('mrp.recent')}
          </h2>
          <Table
            head={[t('mrp.c_order'), t('mrp.product'), t('mrp.c_made'), t('mrp.c_finished'), t('mrp.unit_cost'), t('mrp.cost')]}
            phoneHide={[0, 3, 4]}
            left={[0, 1]}
            rows={o.recent.map((r) => ({
              key: r.id,
              onClick: () => onOpen(r.id),
              cells: [
                <span className="tabular strong">{moName(r.number)}</span>,
                <span className="vat-party">
                  <span className="strong">{r.item_name}</span>
                  <span className="muted">{r.lot ?? r.bom_name}</span>
                </span>,
                <span className="tabular">{amount(r.produced ?? 0, r.unit)}</span>,
                <span className="tabular">{r.finished_at ? dateTime(r.finished_at) : ''}</span>,
                <span className="tabular">{t('mrp.per', { amount: money(Math.round(r.unit_cost)), unit: unitLabel(r.unit) })}</span>,
                <span className="tabular strong">{money(r.cost)}</span>,
              ],
            }))}
          />
        </section>
      ) : null}
    </>
  );
}

function OrderList({ title, icon, rows, empty, onOpen }: { title: string; icon: IconName; rows: OrderRow[]; empty: string; onOpen: (id: string) => void }) {
  return (
    <section className="panel" aria-label={title}>
      <h2 className="htl-desk-title">
        <Icon name={icon} size={18} /> {title}
        <span className="count">{rows.length}</span>
      </h2>
      {rows.length ? (
        <div className="list-card">
          {rows.map((r) => (
            <button key={r.id} className="list-row" onClick={() => onOpen(r.id)}>
              <span className="htl-room-chip mrp-chip">{moName(r.number)}</span>
              <span className="list-main">
                <span className="list-title">
                  {r.item_name} · <span className="tabular">{amount(r.qty, r.unit)}</span>
                </span>
                <span className="list-sub">
                  {[
                    r.state === 'in_progress' && r.steps ? t('mrp.steps_done', { done: r.steps_done, of: r.steps }) : short(r.planned_on),
                    r.parent_number ? t('mrp.for_order', { n: moName(r.parent_number) }) : null,
                    r.location_name,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              {r.late ? <StateBadge s={r.state} late /> : r.short ? <StatusBadge tone="warn" icon="alert" label={t('mrp.short')} /> : null}
              <Icon name="chevronRight" size={18} />
            </button>
          ))}
        </div>
      ) : (
        <p className="muted htl-empty">{empty}</p>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- Orders */

function OrdersTab({ params, set, onOpen }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void }) {
  const state = (FILTERS.includes(params.get('state') as Filter) ? params.get('state') : 'open') as Filter;
  const [q, setQ] = useState(params.get('q') ?? '');
  const [query, setQuery] = useState(q);
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data } = useAction<{ today: string; orders: OrderRow[] }>('mrp.orders', { state, q: query });
  const { multi } = usePlaces();
  const rows = data?.orders ?? null;
  const csv = () =>
    downloadCsv(
      `${t('mrp.file')}.csv`,
      [t('mrp.c_order'), t('mrp.product'), t('mrp.c_qty'), t('mrp.unit'), t('mrp.planned_on'), t('mrp.c_state'), t('mrp.c_made'), t('mrp.lot'), t('mrp.materials'), t('mrp.labour'), t('mrp.overhead'), t('mrp.cost')],
      (rows ?? []).map((r) => [moName(r.number), r.item_name, r.qty, unitLabel(r.unit), r.planned_on, t(`mrp.st_${r.state}` as MessageKey), r.produced ?? '', r.lot ?? '', r.materials / 100, r.labour / 100, r.overhead / 100, r.cost / 100]),
    );
  return (
    <>
      <div className="toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('mrp.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="tab" aria-selected={state === f} className={state === f ? 'active' : ''} onClick={() => set({ state: f === 'open' ? null : f })}>
              {t(`mrp.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <span className="row-actions">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mrp.search')} aria-label={t('mrp.search')} />
          </label>
          <Button icon="download" onClick={csv} disabled={!rows?.length}>
            {t('mrp.spreadsheet')}
          </Button>
        </span>
      </div>
      {!rows ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : !rows.length ? (
        <EmptyState icon={query ? 'search' : 'factory'} title={query ? t('mrp.none_found') : t('mrp.no_orders')} body={query ? undefined : t('mrp.no_orders_body')} />
      ) : (
        <Table
          view="manufacturing.orders"
          head={[t('mrp.c_order'), t('mrp.product'), t('mrp.c_qty'), t('mrp.planned_on'), t('mrp.c_state'), t('mrp.cost')]}
          phoneHide={[0, 3, 5]}
          left={[0, 1]}
          rows={rows.map((r) => ({
            key: r.id,
            onClick: () => onOpen(r.id),
            cells: [
              <span className="tabular strong">{moName(r.number)}</span>,
              <span className="vat-party">
                <span className="strong">{r.item_name}</span>
                <span className="muted">{[r.lot, multi ? r.location_name : null, r.parent_number ? t('mrp.for_order', { n: moName(r.parent_number) }) : null].filter(Boolean).join(' · ') || r.bom_name}</span>
              </span>,
              <span className="tabular">{r.state === 'done' ? amount(r.produced ?? 0, r.unit) : amount(r.qty, r.unit)}</span>,
              <span className="tabular">{day(r.planned_on)}</span>,
              <StateBadge s={r.state} late={r.late} />,
              <span className="tabular">{r.state === 'done' ? money(r.cost) : '—'}</span>,
            ],
          }))}
        />
      )}
    </>
  );
}

/* ---------------------------------------------------------------- One order */

function OrderDialog({ id, onClose, onOpen, onChange }: { id: string; onClose: () => void; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<Detail>('mrp.order', { id });
  const { can } = useSession();
  const { busy, run } = useSave();
  const [finishing, setFinishing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [unbuilding, setUnbuilding] = useState(false);
  const changed = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (!data) {
    return (
      <Dialog title={t('mrp.order')} onClose={onClose} size="lg">
        <Skeleton h={320} r="var(--radius-lg)" />
      </Dialog>
    );
  }
  const o = data.order;
  const open = o.state === 'planned' || o.state === 'in_progress';
  const shortLines = data.lines.filter((l) => l.short > 0);
  const start = async () => {
    if (await run(() => action('mrp.start', { id }), t('mrp.started'))) await changed();
  };
  return (
    <>
      <Dialog
        title={`${moName(o.number)} · ${data.item.name}`}
        onClose={onClose}
        size="lg"
        footer={
          <>
            {open && can('mrp.plan') ? (
              <Button variant="ghost" icon="x" onClick={() => setCancelling(true)}>
                {t('mrp.cancel')}
              </Button>
            ) : null}
            <span className="spacer" />
            {o.state === 'planned' && can('mrp.plan') ? (
              <Button icon="edit" onClick={() => setEditing(true)}>
                {t('mrp.change')}
              </Button>
            ) : null}
            {o.state === 'planned' && can('mrp.produce') ? (
              <Button variant="primary" icon="flame" onClick={start} busy={busy}>
                {t('mrp.start')}
              </Button>
            ) : null}
            {o.state === 'in_progress' && can('mrp.produce') ? (
              <Button variant="primary" icon="check" onClick={() => setFinishing(true)}>
                {t('mrp.finish')}
              </Button>
            ) : null}
            {o.state === 'done' && can('mrp.produce') && (o.produced ?? 0) - data.unbuilt > 0 ? (
              <Button icon="undo" onClick={() => setUnbuilding(true)}>
                {t('ub.take_apart')}
              </Button>
            ) : null}
            {!open ? (
              <Button variant="ghost" onClick={onClose}>
                {t('common.close')}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="form-stack">
          <div className="mrp-head">
            <StateBadge s={o.state} late={open && o.late} />
            <span className="tabular strong">{o.state === 'done' ? t('mrp.made_of', { made: amount(o.produced ?? 0, data.item.unit), of: amount(o.qty, data.item.unit) }) : amount(o.qty, data.item.unit)}</span>
            <span className="muted">{data.bom.name}</span>
          </div>
          {open && shortLines.length ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t('mrp.short_note', { list: shortLines.map((l) => `${l.name} −${amount(l.short, l.unit)}`).join(', ') })}</span>
            </p>
          ) : null}
          {o.state === 'cancelled' ? (
            <p className="callout">
              <Icon name="x" size={16} />
              <span>{[t('mrp.cancelled_note'), o.cancel_reason].filter(Boolean).join(' · ')}</span>
            </p>
          ) : null}
          <dl className="appr-facts">
            <dt>{t('mrp.planned_on')}</dt>
            <dd>{day(o.planned_on)}</dd>
            <dt>{t('mrp.where')}</dt>
            <dd>{data.location.name}</dd>
            {o.lot ? (
              <>
                <dt>{t('mrp.lot')}</dt>
                <dd className="tabular">{o.lot}</dd>
              </>
            ) : null}
            {data.parent ? (
              <>
                <dt>{t('mrp.for')}</dt>
                <dd>
                  <button className="link" onClick={() => onOpen(data.parent!.id)}>
                    {moName(data.parent.number)}
                  </button>
                </dd>
              </>
            ) : null}
            {data.children.length ? (
              <>
                <dt>{t('mrp.parts_orders')}</dt>
                <dd>
                  {data.children.map((c, i) => (
                    <span key={c.id}>
                      {i ? ', ' : ''}
                      <button className="link" onClick={() => onOpen(c.id)}>
                        {moName(c.number)} {c.item_name}
                      </button>
                    </span>
                  ))}
                </dd>
              </>
            ) : null}
            {o.started_at ? (
              <>
                <dt>{t('mrp.started_at')}</dt>
                <dd>{[dateTime(o.started_at), data.started_by_name].filter(Boolean).join(' · ')}</dd>
              </>
            ) : null}
            {o.finished_at ? (
              <>
                <dt>{t('mrp.finished_at')}</dt>
                <dd>{[dateTime(o.finished_at), data.finished_by_name].filter(Boolean).join(' · ')}</dd>
              </>
            ) : null}
            {o.note ? (
              <>
                <dt>{t('mrp.note')}</dt>
                <dd>{o.note}</dd>
              </>
            ) : null}
            {data.unbuilt ? (
              <>
                <dt>{t('ub.taken_apart')}</dt>
                <dd className="tabular">{amount(data.unbuilt, data.item.unit)}</dd>
              </>
            ) : null}
          </dl>

          <section className="item-moves">
            <h3>{t('mrp.materials')}</h3>
            <Table
              head={open ? [t('mrp.material'), t('mrp.needed'), t('mrp.on_hand'), t('mrp.value')] : [t('mrp.material'), t('mrp.planned'), t('mrp.used'), t('mrp.value')]}
              phoneHide={[3]}
              left={[0]}
              rows={data.lines.map((l) => ({
                key: l.id,
                cells: open
                  ? [
                      <span className="vat-party">
                        <span className="strong">{l.name}</span>
                        <span className="muted">{l.made_here ? t('mrp.made_here') : t('mrp.per', { amount: money(Math.round(l.current_cost)), unit: unitLabel(l.unit) })}</span>
                      </span>,
                      <span className="tabular">{amount(l.planned, l.unit)}</span>,
                      <span className={`tabular${l.short ? ' warn-text strong' : ''}`}>{amount(l.on_hand, l.unit)}</span>,
                      <span className="tabular">{money(Math.round(l.planned * l.current_cost))}</span>,
                    ]
                  : [
                      <span className="strong">{l.name}</span>,
                      <span className="tabular">{amount(l.planned, l.unit)}</span>,
                      <span className={`tabular${l.consumed !== null && l.consumed > l.planned ? ' warn-text' : ''}`}>{l.consumed === null ? '—' : amount(l.consumed, l.unit)}</span>,
                      <span className="tabular">{o.state === 'done' ? money(l.value) : '—'}</span>,
                    ],
              }))}
            />
          </section>

          {data.steps.length ? (
            <section className="item-moves">
              <h3>{t('mrp.work')}</h3>
              <div className="list-card">
                {data.steps.map((s) => (
                  <StepRow key={s.id} s={s} canRecord={o.state === 'in_progress' && can('mrp.produce')} onDone={changed} />
                ))}
              </div>
            </section>
          ) : null}

          <section className="item-moves">
            <h3>{o.state === 'done' ? t('mrp.what_it_cost') : t('mrp.estimate')}</h3>
            <CostBox cost={o.state === 'done' ? { materials: o.materials, labour: o.labour, overhead: o.overhead, total: o.cost, per_unit: o.unit_cost } : data.estimate} unit={data.item.unit} stock={data.item.unit_cost} />
            {o.state === 'done' && data.moves.length ? <p className="muted small">{t('mrp.moves_note', { n: moName(o.number) })}</p> : null}
          </section>
        </div>
      </Dialog>
      {finishing ? (
        <FinishDialog
          data={data}
          onClose={() => setFinishing(false)}
          onDone={async () => {
            setFinishing(false);
            await changed();
          }}
        />
      ) : null}
      {cancelling ? (
        <ReasonDialog
          title={t('mrp.cancel_title', { n: moName(o.number) })}
          label={t('mrp.cancel_reason')}
          button={t('mrp.cancel')}
          onClose={() => setCancelling(false)}
          onSave={async (reason) => {
            await action('mrp.cancel', { id, reason });
            await changed();
          }}
        />
      ) : null}
      {unbuilding ? (
        <UnbuildDialog
          order={o.id}
          onClose={() => setUnbuilding(false)}
          onDone={async () => {
            setUnbuilding(false);
            await changed();
          }}
        />
      ) : null}
      {editing ? (
        <PlanDialog
          initial={{ id, bom: data.bom.id, qty: o.qty, planned_on: o.planned_on, location: data.location.id, lot: o.lot, note: o.note }}
          today={null}
          onClose={() => setEditing(false)}
          onDone={async () => {
            setEditing(false);
            await changed();
          }}
        />
      ) : null}
    </>
  );
}

function StepRow({ s, canRecord, onDone }: { s: Detail['steps'][number]; canRecord: boolean; onDone: () => Promise<void> }) {
  const [minutes, setMinutes] = useState(String(Math.round(s.planned_minutes)));
  const [edit, setEdit] = useState(false);
  const { busy, run } = useSave();
  const save = async (value: number | null) => {
    if (await run(() => action('mrp.record_step', { step: s.id, minutes: value }))) {
      setEdit(false);
      await onDone();
    }
  };
  const n = num(minutes);
  return (
    <div className="list-row compact-move mrp-step">
      <span className={`list-icon${s.done_at ? ' ok' : ''}`} aria-hidden="true">
        <Icon name={s.done_at ? 'checkCircle' : 'circle'} size={18} />
      </span>
      <span className="list-main">
        <span className="list-title">{s.name}</span>
        <span className="list-sub">
          {[s.workcenter_name, t('mrp.planned_time', { time: hours(s.planned_minutes) }), s.done_at ? t('mrp.took', { time: hours(s.minutes ?? 0) }) : null, s.done_by_name].filter(Boolean).join(' · ')}
        </span>
      </span>
      {canRecord ? (
        edit || !s.done_at ? (
          <span className="mrp-step-entry">
            <input className="input tabular" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} aria-label={t('mrp.minutes_for', { step: s.name })} />
            <span className="muted small">{t('mrp.min_short')}</span>
            <Button size="sm" icon="check" onClick={() => n !== null && n >= 0 && save(n)} busy={busy} disabled={n === null || n < 0}>
              {t('mrp.done')}
            </Button>
          </span>
        ) : (
          <Button size="sm" variant="ghost" icon="undo" onClick={() => save(null)} busy={busy}>
            {t('mrp.undo')}
          </Button>
        )
      ) : null}
    </div>
  );
}

function CostBox({ cost, unit, stock }: { cost: Cost | null; unit: Unit; stock: number }) {
  if (!cost) return null;
  return (
    <dl className="appr-facts mrp-cost">
      <dt>{t('mrp.materials')}</dt>
      <dd className="tabular">{money(cost.materials)}</dd>
      <dt>{t('mrp.labour')}</dt>
      <dd className="tabular">{money(cost.labour)}</dd>
      <dt>{t('mrp.overhead')}</dt>
      <dd className="tabular">{money(cost.overhead)}</dd>
      <dt className="strong">{t('mrp.total')}</dt>
      <dd className="tabular strong">{money(cost.total)}</dd>
      <dt>{t('mrp.unit_cost')}</dt>
      <dd className="tabular">
        {t('mrp.per', { amount: money(Math.round(cost.per_unit)), unit: unitLabel(unit) })}
        {stock ? <span className="muted small"> · {t('mrp.stock_cost_now', { amount: money(Math.round(stock)) })}</span> : null}
      </dd>
    </dl>
  );
}

function FinishDialog({ data, onClose, onDone }: { data: Detail; onClose: () => void; onDone: () => Promise<void> }) {
  const o = data.order;
  const { busy, run } = useSave();
  const [made, setMade] = useState(fmtQty(o.qty));
  const [scrap, setScrap] = useState('');
  const [used, setUsed] = useState<Record<string, string>>(Object.fromEntries(data.lines.map((l) => [l.id, fmtQty(l.planned)])));
  const [lot, setLot] = useState(o.lot ?? '');
  const [tried, setTried] = useState(false);
  const produced = num(made);
  const scrapped = scrap.trim() ? num(scrap) : 0;
  const errors = {
    made: produced === null || produced <= 0 ? t('mrp.qty_wrong') : null,
    scrap: scrapped === null || scrapped < 0 ? t('mrp.qty_wrong') : null,
    lines: data.lines.some((l) => {
      const n = num(used[l.id] ?? '');
      return n === null || n < 0;
    }),
  };
  const save = async () => {
    setTried(true);
    if (errors.made || errors.scrap || errors.lines) return;
    const lines = data.lines.map((l) => ({ line: l.id, consumed: num(used[l.id]!)! })).filter((x) => x.consumed !== data.lines.find((l) => l.id === x.line)!.planned);
    if (await run(() => action('mrp.finish', { id: o.id, produced, scrap: scrapped, lines, lot: lot.trim() || null }), t('mrp.finished'))) await onDone();
  };
  return (
    <Dialog
      title={t('mrp.finish_title', { n: moName(o.number) })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('mrp.finish')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('mrp.finish_hint')}</p>
        <div className="form-grid">
          <Field label={t('mrp.good_made', { unit: unitLabel(data.item.unit) })} error={tried ? errors.made : null} required>
            <input className="input tabular" inputMode="decimal" value={made} onChange={(e) => setMade(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('mrp.scrap', { unit: unitLabel(data.item.unit) })} hint={t('mrp.scrap_hint')} error={tried ? errors.scrap : null}>
            <input className="input tabular" inputMode="decimal" value={scrap} onChange={(e) => setScrap(e.target.value)} placeholder="0" />
          </Field>
          <Field label={t('mrp.lot')} hint={t('mrp.lot_hint')}>
            <input className="input tabular" maxLength={40} value={lot} onChange={(e) => setLot(e.target.value)} />
          </Field>
        </div>
        <section className="item-moves">
          <h3>{t('mrp.really_used')}</h3>
          <div className="mrp-used">
            {data.lines.map((l) => {
              const n = num(used[l.id] ?? '');
              const over = n !== null && n > Math.max(l.on_hand, 0);
              return (
                <Field key={l.id} label={`${l.name} (${unitLabel(l.unit)})`} hint={t('mrp.used_hint', { planned: fmtQty(l.planned), have: fmtQty(Math.max(l.on_hand, 0)) })} error={tried && (n === null || n < 0) ? t('mrp.qty_wrong') : over ? t('mrp.more_than_there') : null}>
                  <input className="input tabular" inputMode="decimal" value={used[l.id] ?? ''} onChange={(e) => setUsed({ ...used, [l.id]: e.target.value })} />
                </Field>
              );
            })}
          </div>
        </section>
        {data.steps.some((s) => !s.done_at) ? <p className="muted small">{t('mrp.untimed_note')}</p> : null}
      </div>
    </Dialog>
  );
}

function ReasonDialog({ title, label, button, onClose, onSave }: { title: string; label: string; button: string; onClose: () => void; onSave: (reason: string) => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const tooShort = reason.trim().length < 3;
  const save = async () => {
    setTried(true);
    if (tooShort) return;
    if (await run(() => onSave(reason.trim()))) onClose();
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.back')}
          </Button>
          <Button variant="danger" onClick={save} busy={busy}>
            {button}
          </Button>
        </>
      }
    >
      <Field label={label} error={tried && tooShort ? t('mrp.reason_short') : null} required>
        <textarea className="input" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Planning */

function PlanDialog({
  initial,
  today,
  onClose,
  onDone,
}: {
  initial: { id?: string; bom?: string; qty?: number; planned_on?: string; location?: string; lot?: string | null; note?: string | null };
  today: string | null;
  onClose: () => void;
  onDone: (id: string) => Promise<void>;
}) {
  const { data: boms } = useAction<BomRow[]>('mrp.boms');
  const { places, here, multi } = usePlaces();
  const { busy, run } = useSave();
  const { fail } = useUi();
  const [bom, setBom] = useState(initial.bom ?? '');
  const [amountText, setAmount] = useState(initial.qty ? fmtQty(initial.qty) : '');
  const [date, setDate] = useState(initial.planned_on ?? today ?? '');
  const [location, setLocation] = useState(initial.location ?? here?.id ?? '');
  const [lot, setLot] = useState(initial.lot ?? '');
  const [note, setNote] = useState(initial.note ?? '');
  const [parts, setParts] = useState(true);
  const [tried, setTried] = useState(false);
  const [preview, setPreview] = useState<BomDetail | null>(null);
  const active = (boms ?? []).filter((b) => b.active || b.id === initial.bom);
  const chosen = active.find((b) => b.id === bom) ?? null;
  const n = num(amountText);
  useEffect(() => {
    if (!bom && active.length === 1) setBom(active[0]!.id);
  }, [bom, active]);
  useEffect(() => {
    if (!chosen && !initial.qty) return;
    if (chosen && !amountText && !initial.qty) setAmount(fmtQty(chosen.output_qty));
  }, [chosen, amountText, initial.qty]);
  useEffect(() => {
    if (!bom || n === null || n <= 0) return setPreview(null);
    const id = setTimeout(() => action<BomDetail>('mrp.bom', { id: bom, qty: n }).then(setPreview, fail), 250);
    return () => clearTimeout(id);
  }, [bom, n, fail]);
  const errors = {
    bom: bom ? null : t('mrp.choose_bom'),
    qty: n === null || n <= 0 ? t('mrp.qty_wrong') : null,
    date: /^\d{4}-\d{2}-\d{2}$/.test(date) ? null : t('form.required'),
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    let id = '';
    const ok = await run(async () => {
      const r = await action<{ order: { id: string }; parts: { number: number }[] }>('mrp.plan', {
        id: initial.id,
        bom,
        qty: n,
        planned_on: date,
        location: location || null,
        lot: lot.trim() || null,
        note: note.trim() || null,
        with_parts: !initial.id && parts,
      });
      id = r.order.id;
      return r;
    }, initial.id ? t('mrp.saved') : t('mrp.planned_ok'));
    if (ok) await onDone(id);
  };
  return (
    <Dialog
      title={initial.id ? t('mrp.change_plan') : t('mrp.plan')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {initial.id ? t('mrp.save') : t('mrp.plan_it')}
          </Button>
        </>
      }
    >
      {!boms ? (
        <Skeleton h={220} r="var(--radius-lg)" />
      ) : !active.length ? (
        <EmptyState icon="layers" title={t('mrp.no_boms')} body={t('mrp.no_boms_plan')} />
      ) : (
        <div className="form-stack">
          <div className="form-grid">
            <Field label={t('mrp.product')} error={tried ? errors.bom : null} required>
              <select className="input" value={bom} onChange={(e) => setBom(e.target.value)} disabled={Boolean(initial.id)} data-autofocus>
                <option value="">{t('mrp.choose')}</option>
                {active.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.item_name} – {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={chosen ? t('mrp.qty_in', { unit: unitLabel(chosen.unit) }) : t('mrp.c_qty')} hint={chosen ? t('mrp.batch_hint', { qty: amount(chosen.output_qty, chosen.unit) }) : undefined} error={tried ? errors.qty : null} required>
              <input className="input tabular" inputMode="decimal" value={amountText} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field label={t('mrp.planned_on')} error={tried ? errors.date : null} required>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            {multi ? (
              <Field label={t('mrp.where')}>
                <select className="input" value={location} onChange={(e) => setLocation(e.target.value)}>
                  {places.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label={t('mrp.lot')} hint={t('mrp.lot_hint')}>
              <input className="input tabular" maxLength={40} value={lot} onChange={(e) => setLot(e.target.value)} />
            </Field>
          </div>
          <Field label={t('mrp.note')}>
            <textarea className="input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {preview ? (
            <section className="item-moves">
              <h3>{t('mrp.needs', { qty: amount(preview.qty, preview.item.unit) })}</h3>
              <Table
                head={[t('mrp.material'), t('mrp.needed'), t('mrp.in_stock'), t('mrp.value')]}
                phoneHide={[3]}
                left={[0]}
                rows={preview.lines.map((l) => {
                  return {
                    key: l.id,
                    cells: [
                      <span className="vat-party">
                        <span className="strong">{l.name}</span>
                        <span className="muted">{l.made_here ? t('mrp.made_here') : l.scrap_pct ? t('mrp.with_loss', { pct: l.scrap_pct }) : ''}</span>
                      </span>,
                      <span className="tabular">{amount(l.qty, l.unit)}</span>,
                      <StockCell item={l.item} unit={l.unit} need={l.qty} />,
                      <span className="tabular">{money(l.value)}</span>,
                    ],
                  };
                })}
              />
              <CostBox cost={preview.cost} unit={preview.item.unit} stock={preview.item.unit_cost} />
              {!initial.id && preview.lines.some((l) => l.made_here) ? (
                <label className="check-row">
                  <input type="checkbox" checked={parts} onChange={(e) => setParts(e.target.checked)} /> {t('mrp.with_parts')}
                </label>
              ) : null}
            </section>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Taking apart */

function UnbuildTab({ onOpen, onChange }: { onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{ items: UnbuildRow[]; total: number }>('mrp.unbuilds', {});
  const { can } = useSession();
  const [taking, setTaking] = useState(false);
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  return (
    <section className="panel ub">
      <div className="panel-head">
        <div>
          <h2>{t('ub.title')}</h2>
          <p className="muted">{t('ub.sub')}</p>
        </div>
        {can('mrp.produce') ? (
          <Button variant="primary" icon="undo" onClick={() => setTaking(true)}>
            {t('ub.take_apart')}
          </Button>
        ) : null}
      </div>
      {!data.items.length ? (
        <EmptyState icon="undo" title={t('ub.empty')} body={t('ub.empty_body')} />
      ) : (
        <ul className="ub-list">
          {data.items.map((u) => (
            <li key={u.id} className="ub-row">
              <div className="ub-row-head">
                <span className="ub-no tabular">{`UB-${u.number}`}</span>
                <strong>
                  {amount(u.qty, u.unit)} {u.item_name}
                </strong>
                <span className="tabular strong ub-value">{money(u.value)}</span>
              </div>
              <p className="ub-reason">{u.reason}</p>
              <ul className="ub-parts" aria-label={t('ub.came_back')}>
                {u.lines.map((l) => (
                  <li key={l.item} className={l.qty < l.expected ? 'short' : undefined}>
                    <Icon name={l.qty > 0 ? 'arrowDown' : 'x'} size={14} />
                    <span>{l.name}</span>
                    <span className="tabular">{l.qty < l.expected ? t('ub.of', { got: amount(l.qty, l.unit), of: amount(l.expected, l.unit) }) : amount(l.qty, l.unit)}</span>
                  </li>
                ))}
              </ul>
              <p className="muted small">
                {[dateTime(u.at), u.by_name, u.location_name].filter(Boolean).join(' · ')}
                {' · '}
                {u.order ? (
                  <button className="link" onClick={() => onOpen(u.order!)}>
                    {t('ub.from_order', { n: moName(u.order_number!) })}
                  </button>
                ) : (
                  t('ub.from_bom', { name: u.bom_name })
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
      {taking ? (
        <UnbuildDialog
          onClose={() => setTaking(false)}
          onDone={async () => {
            setTaking(false);
            await Promise.all([reload(), onChange()]);
          }}
        />
      ) : null}
    </section>
  );
}

/** Takes products apart: from a finished order (what it really used) or from a bill of materials. */
function UnbuildDialog({ order, onClose, onDone }: { order?: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { data: boms } = useAction<BomRow[]>('mrp.boms');
  const { places, here, multi } = usePlaces();
  const { busy, run } = useSave();
  const { fail } = useUi();
  const [bom, setBom] = useState('');
  const [location, setLocation] = useState(here?.id ?? '');
  const [p, setP] = useState<UnbuildPreview | null>(null);
  const [qtyText, setQty] = useState('1');
  const [back, setBack] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const active = (boms ?? []).filter((b) => b.active);
  useEffect(() => {
    if (!order && !bom) return setP(null);
    action<UnbuildPreview>('mrp.unbuild_preview', order ? { order } : { bom, location: location || null }).then((x) => {
      setP(x);
      setBack({});
    }, fail);
  }, [order, bom, location, fail]);
  const n = num(qtyText);
  const expected = (per: number) => (n && n > 0 ? Math.round(per * n * 1000) / 1000 : 0);
  const got = (part: UnbuildPreview['parts'][number]) => (back[part.item] === undefined ? expected(part.per_unit) : num(back[part.item]!));
  const errors = {
    source: order || bom ? null : t('mrp.choose_bom'),
    qty: n === null || n <= 0 ? t('mrp.qty_wrong') : p && n > p.max ? t('ub.too_many', { max: amount(p.max, p.item.unit) }) : null,
    lines: !!p && p.parts.some((x) => {
      const v = got(x);
      return v === null || v < 0;
    }),
    nothing: !!p && p.parts.every((x) => (got(x) ?? 0) <= 0),
    reason: reason.trim().length < 3 ? t('ub.need_reason') : null,
  };
  const save = async () => {
    setTried(true);
    if (!p || errors.source || errors.qty || errors.lines || errors.nothing || errors.reason) return;
    const lines = p.parts.filter((x) => back[x.item] !== undefined).map((x) => ({ item: x.item, qty: got(x)! }));
    const input = { ...(order ? { order } : { bom, location: location || null }), qty: n, lines, reason: reason.trim() };
    if (await run(() => action('mrp.unbuild', input), t('ub.done'))) await onDone();
  };
  return (
    <Dialog
      title={p?.order ? t('ub.title_order', { n: moName(p.order.number), name: p.item.name }) : t('ub.take_apart')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="undo" onClick={save} busy={busy} disabled={!p || p.max <= 0}>
            {t('ub.take_apart')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('ub.hint')}</p>
        {!order ? (
          !boms ? (
            <Skeleton h={60} r="var(--radius-md)" />
          ) : (
            <div className="form-grid">
              <Field label={t('mrp.product')} error={tried ? errors.source : null} required>
                <select className="input" value={bom} onChange={(e) => setBom(e.target.value)} data-autofocus>
                  <option value="">{t('mrp.choose')}</option>
                  {active.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.item_name} – {b.name}
                    </option>
                  ))}
                </select>
              </Field>
              {multi ? (
                <Field label={t('mrp.where')}>
                  <select className="input" value={location} onChange={(e) => setLocation(e.target.value)}>
                    {places.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}
            </div>
          )
        ) : null}
        {!p ? (
          order ? <Skeleton h={200} r="var(--radius-md)" /> : null
        ) : (
          <>
            {p.max <= 0 ? (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} /> {p.order ? t('ub.none_left_order') : t('ub.none_here', { name: p.item.name, place: p.location.name })}
              </p>
            ) : null}
            <div className="form-grid">
              <Field
                label={t('ub.how_many', { unit: unitLabel(p.item.unit) })}
                hint={p.order ? t('ub.left_order', { left: amount(p.order.left, p.item.unit), have: amount(Math.max(p.item.on_hand, 0), p.item.unit) }) : t('ub.in_stock', { have: amount(Math.max(p.item.on_hand, 0), p.item.unit), place: p.location.name })}
                error={tried || (n !== null && p && n > p.max) ? errors.qty : null}
                required
              >
                <input className="input tabular" inputMode="decimal" value={qtyText} onChange={(e) => setQty(e.target.value)} data-autofocus={order ? true : undefined} />
              </Field>
              <div className="ub-value-box">
                <span className="muted small">{t('ub.value')}</span>
                <strong className="tabular">{money(Math.round((n && n > 0 ? n : 0) * p.item.unit_cost))}</strong>
                <span className="muted small">{t('ub.value_hint')}</span>
              </div>
            </div>
            <section className="item-moves">
              <h3>{t('ub.comes_back')}</h3>
              <p className="muted small">{t('ub.comes_back_hint')}</p>
              <div className="ub-back">
                {p.parts.map((x) => {
                  const v = got(x);
                  return (
                    <Field
                      key={x.item}
                      label={`${x.name} (${unitLabel(x.unit)})`}
                      hint={t('ub.holds', { qty: amount(expected(x.per_unit), x.unit) })}
                      error={tried && (v === null || v < 0) ? t('mrp.qty_wrong') : null}
                    >
                      <input
                        className="input tabular"
                        inputMode="decimal"
                        value={back[x.item] ?? fmtQty(expected(x.per_unit))}
                        onChange={(e) => setBack({ ...back, [x.item]: e.target.value })}
                      />
                    </Field>
                  );
                })}
              </div>
              {tried && errors.nothing ? (
                <p className="form-error" role="alert">
                  {t('ub.nothing')}
                </p>
              ) : null}
            </section>
            <Field label={t('ub.reason')} hint={t('ub.reason_hint')} error={tried ? errors.reason : null} required>
              <textarea className="input" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

function StockCell({ item, unit, need }: { item: string; unit: Unit; need: number }) {
  const list = useItems();
  const it = list?.find((x) => x.id === item);
  if (!it) return <span className="muted">…</span>;
  const enough = it.on_hand >= need;
  return <span className={`tabular${enough ? '' : ' warn-text strong'}`}>{amount(it.on_hand, unit)}</span>;
}

let itemsCache: Promise<StockItem[]> | null = null;
function useItems(fresh = false) {
  const [items, setItems] = useState<StockItem[] | null>(null);
  const { fail } = useUi();
  useEffect(() => {
    if (fresh || !itemsCache) itemsCache = action<StockItem[]>('mrp.items');
    itemsCache.then(setItems, (e) => {
      itemsCache = null;
      fail(e);
    });
  }, [fresh, fail]);
  return items;
}

/* ---------------------------------------------------------------- Bills of materials */

function BomsTab({ onPlan }: { onPlan?: (bom: string) => void }) {
  const { data, reload } = useAction<BomRow[]>('mrp.boms');
  const { can } = useSession();
  const [edit, setEdit] = useState<{ id: string | null } | null>(null);
  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  return (
    <section className="panel">
      <div className="prot-history-head mrp-section-head">
        <div>
          <h2 className="rep-sub">{t('mrp.boms')}</h2>
          <p className="muted small">{t('mrp.boms_hint')}</p>
        </div>
        {can('mrp.configure') ? (
          <Button icon="plus" onClick={() => setEdit({ id: null })}>
            {t('mrp.new_bom')}
          </Button>
        ) : null}
      </div>
      {data.length ? (
        <Table
          view="manufacturing.boms"
          head={[t('mrp.product'), t('mrp.batch'), t('mrp.materials'), t('mrp.unit_cost'), t('mrp.stock_cost'), t('mrp.in_stock')]}
          phoneHide={[1, 2, 4]}
          left={[0]}
          rows={data.map((b) => ({
            key: b.id,
            onClick: () => setEdit({ id: b.id }),
            cells: [
              <span className="vat-party">
                <span className={b.active ? 'strong' : 'muted'}>{b.item_name}</span>
                <span className="muted">{b.active ? b.name : t('mrp.not_used')}</span>
              </span>,
              <span className="tabular">{amount(b.output_qty, b.unit)}</span>,
              <span className="muted">{[tn('mrp.n_materials', b.materials), b.steps ? tn('mrp.n_steps', b.steps) : null].filter(Boolean).join(' · ')}</span>,
              <span className="tabular strong">{t('mrp.per', { amount: money(Math.round(b.per_unit)), unit: unitLabel(b.unit) })}</span>,
              <span className="tabular">{b.stock_cost ? money(Math.round(b.stock_cost)) : '—'}</span>,
              <span className="tabular">{amount(b.on_hand, b.unit)}</span>,
            ],
          }))}
        />
      ) : (
        <EmptyState
          icon="layers"
          title={t('mrp.no_boms')}
          body={t('mrp.no_boms_body')}
          action={
            can('mrp.configure') ? (
              <Button variant="primary" icon="plus" onClick={() => setEdit({ id: null })}>
                {t('mrp.new_bom')}
              </Button>
            ) : undefined
          }
        />
      )}
      {edit ? (
        <BomForm
          id={edit.id}
          canEdit={can('mrp.configure')}
          onPlan={onPlan}
          onClose={() => setEdit(null)}
          onDone={async () => {
            setEdit(null);
            itemsCache = null;
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

interface LineDraft {
  key: number;
  item: string;
  qty: string;
  scrap: string;
}
interface StepDraft {
  key: number;
  name: string;
  workcenter: string;
  minutes: string;
}
let seq = 0;

function BomForm({ id, canEdit, onPlan, onClose, onDone }: { id: string | null; canEdit: boolean; onPlan?: (bom: string) => void; onClose: () => void; onDone: () => Promise<void> }) {
  const items = useItems(true);
  const { data: centres } = useAction<WorkCenter[]>('mrp.workcenters');
  const [detail, setDetail] = useState<BomDetail | null>(null);
  const { fail } = useUi();
  const { busy, run } = useSave();
  const [product, setProduct] = useState('');
  const [newName, setNewName] = useState('');
  const [newUnit, setNewUnit] = useState<Unit>('pcs');
  const [name, setName] = useState('');
  const [output, setOutput] = useState('1');
  const [overhead, setOverhead] = useState('');
  const [note, setNote] = useState('');
  const [active, setActive] = useState(true);
  const [lines, setLines] = useState<LineDraft[]>([{ key: seq++, item: '', qty: '', scrap: '' }]);
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (!id) return;
    action<BomDetail>('mrp.bom', { id }).then((d) => {
      setDetail(d);
      setProduct(d.item.id);
      setName(d.bom.name);
      setOutput(fmtQty(d.bom.output_qty));
      setOverhead(lek(d.bom.overhead));
      setNote(d.bom.note ?? '');
      setActive(d.bom.active);
      setLines(d.lines.map((l) => ({ key: seq++, item: l.item, qty: fmtQty(l.per_batch), scrap: l.scrap_pct ? fmtQty(l.scrap_pct) : '' })));
      setSteps(d.steps.map((s) => ({ key: seq++, name: s.name, workcenter: s.workcenter ?? '', minutes: fmtQty(s.per_batch) })));
    }, fail);
  }, [id, fail]);
  const itemOf = (x: string) => items?.find((i) => i.id === x);
  const unit: Unit = product === '__new' ? newUnit : itemOf(product)?.unit ?? 'pcs';
  const overheadMinor = overhead.trim() ? parseMoney(overhead) : 0;
  const errors = {
    product: product === '__new' ? (newName.trim() ? null : t('form.required')) : product ? null : t('mrp.choose_product'),
    name: name.trim() ? null : t('form.required'),
    output: (num(output) ?? 0) > 0 ? null : t('mrp.qty_wrong'),
    overhead: overheadMinor === null ? t('mrp.amount_wrong') : null,
    lines: !lines.some((l) => l.item) ? t('mrp.add_material') : lines.some((l) => l.item && (!(num(l.qty)! > 0) || (l.scrap.trim() && !(num(l.scrap)! >= 0 && num(l.scrap)! <= 100)))) ? t('mrp.lines_wrong') : null,
    steps: steps.some((s) => (s.name.trim() || s.minutes.trim()) && (!s.name.trim() || !(num(s.minutes)! >= 0))) ? t('mrp.steps_wrong') : null,
  };
  // A rough cost while typing: materials at today's average cost, work at each centre's rate.
  const est = useMemo(() => {
    const m = lines.reduce((s, l) => {
      const it = itemOf(l.item);
      const q = num(l.qty) ?? 0;
      return s + (it ? q * (1 + (num(l.scrap) ?? 0) / 100) * it.unit_cost : 0);
    }, 0);
    const w = steps.reduce((s, x) => s + ((num(x.minutes) ?? 0) / 60) * (centres?.find((c) => c.id === x.workcenter)?.cost_per_hour ?? 0), 0);
    const total = Math.round(m) + Math.round(w) + (overheadMinor ?? 0);
    const out = num(output) ?? 0;
    return { materials: Math.round(m), labour: Math.round(w), overhead: overheadMinor ?? 0, total, per_unit: out > 0 ? total / out : 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, steps, centres, overheadMinor, output, items]);
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const ok = await run(
      () =>
        action('mrp.save_bom', {
          id: id ?? undefined,
          item: product && product !== '__new' ? product : undefined,
          new_item: product === '__new' ? { name: newName.trim(), unit: newUnit } : undefined,
          name: name.trim(),
          output_qty: num(output),
          overhead: overheadMinor ?? 0,
          note: note.trim() || null,
          active,
          lines: lines.filter((l) => l.item).map((l) => ({ item: l.item, qty: num(l.qty), scrap_pct: num(l.scrap) ?? 0 })),
          steps: steps.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), workcenter: s.workcenter || null, minutes: num(s.minutes) ?? 0 })),
        }),
      t('mrp.saved'),
    );
    if (ok) await onDone();
  };
  const choices = (items ?? []).filter((i) => i.id !== product);
  const loading = !items || (id && !detail);
  return (
    <Dialog
      title={detail ? `${detail.item.name} – ${detail.bom.name}` : t('mrp.new_bom')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          {detail && onPlan && detail.bom.active ? (
            <Button
              variant="ghost"
              icon="calendar"
              onClick={() => {
                onClose();
                onPlan(detail.bom.id);
              }}
            >
              {t('mrp.plan_this')}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button variant="ghost" onClick={onClose}>
            {canEdit ? t('common.cancel') : t('common.close')}
          </Button>
          {canEdit ? (
            <Button variant="primary" icon="check" onClick={save} busy={busy}>
              {t('mrp.save')}
            </Button>
          ) : null}
        </>
      }
    >
      {loading ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : (
        <fieldset className="form-stack bare" disabled={!canEdit}>
          <div className="form-grid">
            <Field label={t('mrp.product')} hint={id ? undefined : t('mrp.product_hint')} error={tried ? errors.product : null} required>
              <select className="input" value={product} onChange={(e) => setProduct(e.target.value)} disabled={Boolean(id)} data-autofocus>
                <option value="">{t('mrp.choose')}</option>
                {!id ? <option value="__new">{t('mrp.new_product')}</option> : null}
                {(items ?? []).map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </Field>
            {product === '__new' ? (
              <>
                <Field label={t('mrp.new_product_name')} error={tried && !newName.trim() ? t('form.required') : null} required>
                  <input className="input" maxLength={80} value={newName} onChange={(e) => setNewName(e.target.value)} />
                </Field>
                <Field label={t('mrp.unit')}>
                  <select className="input" value={newUnit} onChange={(e) => setNewUnit(e.target.value as Unit)}>
                    {UNITS.map((u) => (
                      <option key={u} value={u}>
                        {unitLabel(u)}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            ) : null}
            <Field label={t('mrp.bom_name')} hint={t('mrp.bom_name_hint')} error={tried ? errors.name : null} required>
              <input className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('mrp.makes', { unit: unitLabel(unit) })} hint={t('mrp.makes_hint')} error={tried ? errors.output : null} required>
              <input className="input tabular" inputMode="decimal" value={output} onChange={(e) => setOutput(e.target.value)} />
            </Field>
            <Field label={t('mrp.overhead_batch')} hint={t('mrp.overhead_hint')} error={tried ? errors.overhead : null}>
              <input className="input tabular" inputMode="decimal" value={overhead} onChange={(e) => setOverhead(e.target.value)} placeholder="0" />
            </Field>
          </div>

          <section className="item-moves">
            <h3>{t('mrp.materials_batch')}</h3>
            {tried && errors.lines ? <p className="field-msg" role="alert">{errors.lines}</p> : null}
            <div className="mrp-lines" role="table" aria-label={t('mrp.materials_batch')}>
              <div className="mrp-line head" role="row">
                <span role="columnheader">{t('mrp.material')}</span>
                <span role="columnheader">{t('mrp.c_qty')}</span>
                <span role="columnheader">{t('mrp.loss')}</span>
                <span role="columnheader" className="mrp-hide-phone">
                  {t('mrp.value')}
                </span>
                <span />
              </div>
              {lines.map((l, i) => {
                const it = itemOf(l.item);
                const q = num(l.qty) ?? 0;
                return (
                  <div key={l.key} className="mrp-line" role="row">
                    <select className="input" value={l.item} onChange={(e) => setLines(lines.map((x) => (x.key === l.key ? { ...x, item: e.target.value } : x)))} aria-label={t('mrp.material_n', { n: i + 1 })}>
                      <option value="">{t('mrp.choose')}</option>
                      {choices
                        .filter((c) => c.id === l.item || !lines.some((x) => x.item === c.id))
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <span className="mrp-qty">
                      <input className="input tabular" inputMode="decimal" value={l.qty} onChange={(e) => setLines(lines.map((x) => (x.key === l.key ? { ...x, qty: e.target.value } : x)))} aria-label={t('mrp.qty_of', { n: i + 1 })} />
                      <span className="muted small">{it ? unitLabel(it.unit) : ''}</span>
                    </span>
                    <span className="mrp-qty">
                      <input className="input tabular" inputMode="decimal" value={l.scrap} placeholder="0" onChange={(e) => setLines(lines.map((x) => (x.key === l.key ? { ...x, scrap: e.target.value } : x)))} aria-label={t('mrp.loss_of', { n: i + 1 })} />
                      <span className="muted small">%</span>
                    </span>
                    <span className="tabular muted mrp-hide-phone">{it && q ? money(Math.round(q * (1 + (num(l.scrap) ?? 0) / 100) * it.unit_cost)) : ''}</span>
                    {canEdit ? (
                      <Button size="sm" variant="ghost" icon="trash" onClick={() => setLines(lines.length > 1 ? lines.filter((x) => x.key !== l.key) : [{ key: seq++, item: '', qty: '', scrap: '' }])} aria-label={t('mrp.remove')} />
                    ) : (
                      <span />
                    )}
                  </div>
                );
              })}
            </div>
            {canEdit ? (
              <Button size="sm" icon="plus" onClick={() => setLines([...lines, { key: seq++, item: '', qty: '', scrap: '' }])}>
                {t('mrp.add_material')}
              </Button>
            ) : null}
          </section>

          <section className="item-moves">
            <h3>{t('mrp.work_batch')}</h3>
            {tried && errors.steps ? <p className="field-msg" role="alert">{errors.steps}</p> : null}
            {steps.length ? (
              <div className="mrp-lines" role="table" aria-label={t('mrp.work_batch')}>
                <div className="mrp-line head" role="row">
                  <span role="columnheader">{t('mrp.step')}</span>
                  <span role="columnheader">{t('mrp.workcenter')}</span>
                  <span role="columnheader">{t('mrp.minutes')}</span>
                  <span role="columnheader" className="mrp-hide-phone">
                    {t('mrp.value')}
                  </span>
                  <span />
                </div>
                {steps.map((s, i) => {
                  const rate = centres?.find((c) => c.id === s.workcenter)?.cost_per_hour ?? 0;
                  return (
                    <div key={s.key} className="mrp-line" role="row">
                      <input className="input" maxLength={80} value={s.name} onChange={(e) => setSteps(steps.map((x) => (x.key === s.key ? { ...x, name: e.target.value } : x)))} aria-label={t('mrp.step_n', { n: i + 1 })} />
                      <select className="input" value={s.workcenter} onChange={(e) => setSteps(steps.map((x) => (x.key === s.key ? { ...x, workcenter: e.target.value } : x)))} aria-label={t('mrp.workcenter')}>
                        <option value="">{t('mrp.no_centre')}</option>
                        {(centres ?? []).filter((c) => c.active || c.id === s.workcenter).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <span className="mrp-qty">
                        <input className="input tabular" inputMode="numeric" value={s.minutes} onChange={(e) => setSteps(steps.map((x) => (x.key === s.key ? { ...x, minutes: e.target.value } : x)))} aria-label={t('mrp.minutes_n', { n: i + 1 })} />
                        <span className="muted small">{t('mrp.min_short')}</span>
                      </span>
                      <span className="tabular muted mrp-hide-phone">{rate && num(s.minutes) ? money(Math.round(((num(s.minutes) ?? 0) / 60) * rate)) : ''}</span>
                      {canEdit ? <Button size="sm" variant="ghost" icon="trash" onClick={() => setSteps(steps.filter((x) => x.key !== s.key))} aria-label={t('mrp.remove')} /> : <span />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="muted small">{t('mrp.no_steps')}</p>
            )}
            {canEdit ? (
              <Button size="sm" icon="plus" onClick={() => setSteps([...steps, { key: seq++, name: '', workcenter: centres?.find((c) => c.active)?.id ?? '', minutes: '' }])}>
                {t('mrp.add_step')}
              </Button>
            ) : null}
          </section>

          <section className="item-moves">
            <h3>{t('mrp.batch_cost', { qty: amount(num(output) ?? 0, unit) })}</h3>
            <CostBox cost={est} unit={unit} stock={product && product !== '__new' ? itemOf(product)?.unit_cost ?? 0 : 0} />
          </section>

          <Field label={t('mrp.note')}>
            <textarea className="input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <label className="check-row">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('mrp.bom_active')}
          </label>
          {detail?.used_in.length ? <p className="muted small">{t('mrp.used_in', { list: detail.used_in.map((u) => u.item_name).join(', ') })}</p> : null}
        </fieldset>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Work centres */

function WorkCentersTab() {
  const { data, reload } = useAction<WorkCenter[]>('mrp.workcenters');
  const { can } = useSession();
  const [edit, setEdit] = useState<{ wc: WorkCenter | null } | null>(null);
  if (!data) return <Skeleton h={260} r="var(--radius-lg)" />;
  return (
    <section className="panel">
      <div className="prot-history-head mrp-section-head">
        <div>
          <h2 className="rep-sub">{t('mrp.workcenters')}</h2>
          <p className="muted small">{t('mrp.workcenters_hint')}</p>
        </div>
        {can('mrp.configure') ? (
          <Button icon="plus" onClick={() => setEdit({ wc: null })}>
            {t('mrp.new_workcenter')}
          </Button>
        ) : null}
      </div>
      {data.length ? (
        <Table
          head={[t('mrp.workcenter'), t('mrp.rate'), t('mrp.hours_day'), t('mrp.c_boms'), t('mrp.open_work')]}
          phoneHide={[2, 3]}
          left={[0]}
          rows={data.map((w) => ({
            key: w.id,
            onClick: can('mrp.configure') ? () => setEdit({ wc: w }) : undefined,
            cells: [
              <span className="vat-party">
                <span className={w.active ? 'strong' : 'muted'}>{w.name}</span>
                <span className="muted">{w.active ? w.note ?? '' : t('mrp.not_used')}</span>
              </span>,
              <span className="tabular">{t('mrp.per_hour', { amount: money(w.cost_per_hour) })}</span>,
              <span className="tabular">{fmtQty(w.hours_per_day)}</span>,
              <span className="tabular">{w.boms}</span>,
              <span className="tabular">{w.open_minutes ? hours(w.open_minutes) : '—'}</span>,
            ],
          }))}
        />
      ) : (
        <EmptyState icon="settings" title={t('mrp.no_workcenters')} body={t('mrp.no_workcenters_body')} />
      )}
      {edit ? (
        <WorkCenterForm
          wc={edit.wc}
          onClose={() => setEdit(null)}
          onDone={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

function WorkCenterForm({ wc, onClose, onDone }: { wc: WorkCenter | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(wc?.name ?? '');
  const [rate, setRate] = useState(wc ? lek(wc.cost_per_hour) : '');
  const [perDay, setPerDay] = useState(wc ? fmtQty(wc.hours_per_day) : '8');
  const [note, setNote] = useState(wc?.note ?? '');
  const [active, setActive] = useState(wc?.active ?? true);
  const [tried, setTried] = useState(false);
  const minor = rate.trim() ? parseMoney(rate) : 0;
  const h = num(perDay);
  const errors = {
    name: name.trim() ? null : t('form.required'),
    rate: minor === null ? t('mrp.amount_wrong') : null,
    hours: h !== null && h > 0 && h <= 24 ? null : t('mrp.hours_wrong'),
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    if (await run(() => action('mrp.save_workcenter', { id: wc?.id, name: name.trim(), cost_per_hour: minor ?? 0, hours_per_day: h, note: note.trim() || null, active }), t('mrp.saved'))) await onDone();
  };
  return (
    <Dialog
      title={wc ? wc.name : t('mrp.new_workcenter')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('mrp.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('mrp.wc_name')} error={tried ? errors.name : null} required>
            <input className="input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('mrp.rate')} hint={t('mrp.rate_hint')} error={tried ? errors.rate : null}>
            <input className="input tabular" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0" />
          </Field>
          <Field label={t('mrp.hours_day')} hint={t('mrp.hours_day_hint')} error={tried ? errors.hours : null} required>
            <input className="input tabular" inputMode="decimal" value={perDay} onChange={(e) => setPerDay(e.target.value)} />
          </Field>
        </div>
        <Field label={t('mrp.note')}>
          <textarea className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <label className="check-row">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('mrp.wc_active')}
        </label>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Reports */

function ReportsTab({ today }: { today: string }) {
  const [range, setRange] = useState<'month' | 'last' | 'days30' | 'year'>('month');
  const span = useMemo(() => {
    const first = `${today.slice(0, 8)}01`;
    if (range === 'last') {
      const prev = `${addDays(first, -1).slice(0, 8)}01`;
      return { from: prev, to: addDays(first, -1) };
    }
    if (range === 'days30') return { from: addDays(today, -29), to: today };
    if (range === 'year') return { from: `${today.slice(0, 4)}-01-01`, to: today };
    return { from: first, to: today };
  }, [today, range]);
  const [r, setR] = useState<Report | null>(null);
  const { fail } = useUi();
  useEffect(() => {
    setR(null);
    action<Report>('mrp.report', span).then(setR, fail);
  }, [span, fail]);
  const csv = () =>
    r &&
    downloadCsv(
      `${t('mrp.report_file')}-${r.from}-${r.to}.csv`,
      [t('mrp.product'), t('mrp.c_orders'), t('mrp.c_made'), t('mrp.unit'), t('mrp.c_scrap'), t('mrp.cost'), t('mrp.unit_cost')],
      r.by_item.map((x) => [x.name, x.orders, x.produced, unitLabel(x.unit), x.scrap, x.cost / 100, Math.round(x.unit_cost) / 100]),
    );
  return (
    <>
      <div className="toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('mrp.period')}>
          {(['month', 'last', 'days30', 'year'] as const).map((k) => (
            <button key={k} role="tab" aria-selected={range === k} className={range === k ? 'active' : ''} onClick={() => setRange(k)}>
              {t(`mrp.r_${k}` as MessageKey)}
            </button>
          ))}
        </div>
        <Button icon="download" onClick={csv} disabled={!r?.by_item.length}>
          {t('mrp.spreadsheet')}
        </Button>
      </div>
      {!r ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : !r.totals.orders ? (
        <EmptyState icon="chart" title={t('mrp.nothing_made')} body={t('mrp.nothing_made_body', { from: day(r.from), to: day(r.to) })} />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('mrp.at_a_glance')}>
            <Kpi label={t('mrp.k_cost')} value={money(r.totals.cost)} hint={tn('mrp.k_orders_done', r.totals.orders)} />
            <Kpi label={t('mrp.materials')} value={money(r.totals.materials)} hint={r.totals.cost ? t('mrp.k_share', { pct: Math.round((r.totals.materials / r.totals.cost) * 100) }) : undefined} />
            <Kpi label={t('mrp.labour')} value={money(r.totals.labour)} hint={t('mrp.k_hours', { h: oneDec(r.totals.hours) })} />
            <Kpi label={t('mrp.k_scrap_rate')} value={`${r.totals.scrap_pct}%`} hint={t('mrp.k_scrap_hint')} warn={r.totals.scrap_pct > 5} />
          </section>
          <section className="panel rep-wide">
            <div className="panel-head">
              <h2>{t('mrp.cost_by_day')}</h2>
            </div>
            <Bars items={r.days.map((d) => ({ key: d.date, label: short(d.date), value: d.cost, tip: `${day(d.date)}: ${money(d.cost)} · ${tn('mrp.k_orders_done', d.orders)}` }))} />
          </section>
          <section className="panel">
            <h2 className="rep-sub">{t('mrp.by_product')}</h2>
            <Table
              head={[t('mrp.product'), t('mrp.c_orders'), t('mrp.c_made'), t('mrp.c_scrap'), t('mrp.unit_cost'), t('mrp.cost')]}
              phoneHide={[1, 3, 4]}
              left={[0]}
              rows={r.by_item.map((x) => ({
                key: x.item,
                cells: [
                  <span className="strong">{x.name}</span>,
                  <span className="tabular">{x.orders}</span>,
                  <span className="tabular">{amount(x.produced, x.unit)}</span>,
                  <span className="tabular">{x.scrap ? amount(x.scrap, x.unit) : '—'}</span>,
                  <span className="tabular">{money(Math.round(x.unit_cost))}</span>,
                  <span className="tabular strong">{money(x.cost)}</span>,
                ],
              }))}
            />
          </section>
          <div className="mrp-two">
            <section className="panel">
              <h2 className="rep-sub">{t('mrp.material_use')}</h2>
              <p className="muted small">{t('mrp.material_use_hint')}</p>
              <Table
                head={[t('mrp.material'), t('mrp.planned'), t('mrp.used'), t('mrp.variance')]}
                phoneHide={[1]}
                left={[0]}
                rows={r.materials.map((m) => ({
                  key: m.item,
                  cells: [
                    <span className="strong">{m.name}</span>,
                    <span className="tabular">{amount(m.planned, m.unit)}</span>,
                    <span className="tabular">{amount(m.consumed, m.unit)}</span>,
                    <span className={`tabular${m.variance > 0 ? ' warn-text' : ''}`}>{m.variance ? money(m.variance, { sign: true }) : '—'}</span>,
                  ],
                }))}
              />
            </section>
            <section className="panel">
              <h2 className="rep-sub">{t('mrp.work_hours')}</h2>
              <p className="muted small">{t('mrp.work_hours_hint')}</p>
              <Table
                head={[t('mrp.workcenter'), t('mrp.planned'), t('mrp.actual')]}
                left={[0]}
                rows={r.work.map((w) => ({
                  key: w.workcenter ?? 'none',
                  cells: [
                    <span className="strong">{w.name ?? t('mrp.no_centre')}</span>,
                    <span className="tabular">{t('mrp.n_hours', { h: oneDec(w.planned_hours) })}</span>,
                    <span className={`tabular${w.hours > w.planned_hours * 1.1 ? ' warn-text' : ''}`}>{t('mrp.n_hours', { h: oneDec(w.hours) })}</span>,
                  ],
                }))}
              />
            </section>
          </div>
        </>
      )}
    </>
  );
}

